import React, { useEffect } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { useParams } from 'react-router-dom';
import { Box, Container, Typography } from '@material-ui/core';
import Cookie from '../../utils/cookies';

import {
  DashVideo,
  GetPageByUrlArgs,
  GetPassedQuizesByPathwayArgs,
  GetPassedQuizesByPathwayData,
  GetPathwayPageInfoArgs,
  GetPathwayPageInfoData,
  GetPathwaysYouMightAlsoLikeArgs,
  GetPathwaysYouMightAlsoLikeData,
  Pathway,
  Query,
  Quiz,
  QuizPassedResult,
  LearnerInfo,
} from '../../types';
import {
  GET_PASSED_QUIZES_BY_PATHWAY,
  GET_PATHWAY_PAGE_INFO,
  GET_PATHWAYS_YOU_MIGHT_ALSO_LIKE,
} from '../../graphql/pathways';
import { Loader } from '../../components/Loader';
import {
  secondsToHHHmmm,
  secondsToHms,
  translate,
  countResultStars,
  calcVideoProgressAndLinkToShow,
  getCertificatePDFUrl,
} from '../../utils/helpers';
import theme, { colorsPalette } from '../../theme';
import { CategoryLabel } from '../../components/UI/CategoryLabel';
import Breadcrumbs from '../../pages/DiscoverPage/components/Breadcrumbs';
import { Header } from '../../pages/DiscoverPage/components/Header';
import { getCategories } from './components/PathwayVideoCard/PathwayVideoCard';
import { PathwayInformation } from './components/PathwayInformation';
import { PathwayProgress } from './components/PathwayProgress';
import PathwayVideos from './components/PathwayVideos';
import JoinNowBlock from '../../components/JoinNowBlock/JoinNowBlock';
import { PathwaysBlock } from './components/PathwaysBlock';
import { GET_PAGE_BY_URL } from '../../graphql/pages';
import { CustomPage } from '../CustomPage';
import useStyles from './styles';
import { DEFAULT_URL } from '../DiscoverPage/const';
import { PathwayTestBlock } from './components/PathwayTestBlock';
import { CardWrapper } from '../../components/UI/CardWrapper';
import { useHeaderStyles } from './styles';
import { Test } from './const';
import { getPathwayPageJsonData } from './const';
import { JSBlock } from '../../components/JSBlock';
import { PathwayPageSeoBlock } from './components/PathwayPageSeoBlock';
import { TestSuccessBlock } from './components/TestSuccessBlock';
import { generateImageKitUrl } from '../../modules/imageKit';
import { useTeamsShareScriptLoader } from '../../utils/hooks';
import { LAST_VIEWED_PATHWAY_SLUG_COOKIE_NAME } from '../SingleVideoPage/const';
import NotFoundPage from '../NotFoundPage';
import RouteWithCode from '../../components/RouteWithCode/RouteWithCode';

const formatPathwayData = (videos, progressData?, isLoggedIn?) => {
  if (!videos || !videos.length) return [];

  return videos.reduce((all, item) => {
    const id = item?.id;
    const videoUrl = item?.video_url || id;
    const link = isLoggedIn
      ? progressData[id]
        ? progressData[id].link
        : `/videos/${videoUrl}/`
      : '/pricing/';

    const video = {
      ...item,
      link,
      user: item?.user?.name,
      userSlug: item?.user?.slug,
      duration: item?.duration ? secondsToHms(item?.duration) : 0,
      progress: progressData[id] ? progressData[id].progress : 0,
      completed: progressData[id] ? progressData[id].completed : false,
    };
    return [...all, video];
  }, []);
};

const formatProgressData = (progress) => {
  if (!progress || !progress.length) return [];

  return progress?.reduce((all, elem) => {
    const id = elem?.video?.id;
    const videoUrl = elem?.video?.video_url || id;
    const { linkToShow, progressToShow } = calcVideoProgressAndLinkToShow(
      elem?.completed,
      elem?.last_position,
      elem?.video?.duration,
      videoUrl,
      elem?.chapter,
      elem?.time,
    );
    return {
      ...all,
      [id as any]: { link: linkToShow, progress: progressToShow, completed: elem?.completed },
    };
  }, {});
};

export const getNavigationArray = (slug, title) => [
  {
    title: translate('page.discover.discover'),
    url: DEFAULT_URL,
  },
  {
    title: translate('page.pathway.pathways'),
    url: '/pathways',
  },
  {
    title,
    url: `/pathways/${slug}`,
  },
];

export const generateCertificateName = (tenant, pathwayName) => {
  return `${tenant}-${pathwayName}.pdf`;
};

export const onDownloadCertificate = async (certName, certUrl) => {
  const img = await fetch(certUrl);
  const imgBlob = await img?.blob();
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(imgBlob);
  link.download = certName;
  link.click();
};

const generateTestsArr = (
  quizzes?: (Quiz | null)[] | null,
  availableQuizzes?: (Quiz | null)[] | null,
  quizzResults?: (QuizPassedResult | null)[] | null,
): Test[] => {
  let arr: Test[] = [];
  if (quizzes) {
    arr = quizzes.map((item, i) => ({
      id: item?.id,
      title: item?.title,
      canPassTest: !!(
        availableQuizzes && availableQuizzes.find((availableQuiz) => availableQuiz?.id === item?.id)
      ),
      testPassed: !!(quizzResults && quizzResults[i]?.passed),
    }));
  }

  return arr;
};

interface PathwayPageProps {
  userInfo?: LearnerInfo;
  tenantTitle?: string | null;
  tenantName?: string | null;
  tenantCircleUrl?: string | null;
  linkedInId?: string | null;
}

const PathwayPage: React.FC<PathwayPageProps> = ({
  userInfo,
  tenantTitle,
  tenantName,
  tenantCircleUrl,
  linkedInId,
}) => {
  const classes = useStyles();
  const header = useHeaderStyles();
  const params = useParams();

  const slug = params?.slug;
  const hasSubscription = !!(userInfo && userInfo.has_subscription);
  const isLoggedIn = !!userInfo;

  useTeamsShareScriptLoader();

  const { data, loading } = useQuery<GetPathwayPageInfoData, GetPathwayPageInfoArgs>(
    GET_PATHWAY_PAGE_INFO,
    {
      variables: {
        slug,
      },
      fetchPolicy: 'network-only',
    },
  );

  const pathway = data?.getPathwayPageInfo?.pathway;
  const progress = data?.getPathwayPageInfo?.progress;

  const progressData = formatProgressData(progress);
  const pathwayData = formatPathwayData(pathway?.videos, progressData, isLoggedIn);
  const pathwayDuration = secondsToHHHmmm(pathway?.duration);
  const pathwayCount = pathway?.count_videos;
  const pathwayTitle = `${pathwayCount} ${translate('common.videos')} • ${pathwayDuration}`;
  const categories = getCategories(pathway?.categories, pathway?.difficulty, false);
  const navigationArr = getNavigationArray(pathway?.name, slug);
  const quizzIds = pathway?.quizes ? pathway.quizes.map((item) => Number(item?.id)) : [];

  const progressVideo = pathway?.videos
    ? pathway.videos.map((item) => {
        const videoFromProgress =
          progress && progress.find((video) => video?.video?.id === item?.id);
        return {
          video: {
            id: item?.id,
            title: item?.title,
          },
          completed: videoFromProgress?.completed,
        };
      })
    : [];

  const hasProgress = progress && progress.length > 0;

  const { data: quizzesResultData, loading: quizzesResultLoading } = useQuery<
    GetPassedQuizesByPathwayData,
    GetPassedQuizesByPathwayArgs
  >(GET_PASSED_QUIZES_BY_PATHWAY, {
    variables: {
      quiz_ids: quizzIds,
      pathway_id: Number(pathway?.id),
    },
    skip: !hasSubscription || loading,
    fetchPolicy: 'network-only',
  });

  const quizzesResults = quizzesResultData?.GetPassedQuizesResults;

  const tests = generateTestsArr(pathway?.quizes, pathway?.available_quizes, quizzesResults);

  const { data: pageData, loading: pageLoading } = useQuery<
    Pick<Query, 'getPageByUrl'>,
    GetPageByUrlArgs
  >(GET_PAGE_BY_URL, {
    variables: {
      url: '/pathways/',
    },
    skip: hasSubscription,
  });

  const page = pageData?.getPageByUrl;
  //filter seo-block to prevent conflicts with video page SEO
  const blocks = page?.blocks ? page.blocks.filter((item) => item?.type !== 'seo-block') : null;

  const { data: pathwaysData, loading: pathwaysLoading } = useQuery<
    GetPathwaysYouMightAlsoLikeData,
    GetPathwaysYouMightAlsoLikeArgs
  >(GET_PATHWAYS_YOU_MIGHT_ALSO_LIKE, {
    variables: {
      slug: slug,
    },
  });

  const pathways = pathwaysData?.listYouMightAlsoLikePathways;

  useEffect(() => {
    Cookie.set(LAST_VIEWED_PATHWAY_SLUG_COOKIE_NAME, slug);
  }, [slug]);

  if (!pathway && !loading) {
    return (
      <RouteWithCode code={404}>
        <NotFoundPage />
      </RouteWithCode>
    );
  }

  if (loading || pathwaysLoading || pageLoading || quizzesResultLoading) {
    return (
      <>
        {pathway && <PathwayPageSeoBlock pathway={pathway} tenantTitle={tenantTitle} />}
        <Loader />
      </>
    );
  }

  const scriptData = {
    customScriptType: 'application/ld+json',
    js_code: getPathwayPageJsonData(pathway?.name, pathway?.description, pathway?.cpd_credits),
  };

  return (
    <>
      <PathwayPageSeoBlock pathway={pathway} tenantTitle={tenantTitle} />
      <JSBlock data={scriptData} />
      <Box>
        <Header title={''} imageUrl={pathway?.image_url}>
          <Container>
            {isLoggedIn && <Breadcrumbs navigation={navigationArr} />}
            <Typography variant="h1" className={header.title}>
              {pathway?.name}
            </Typography>
            <Typography className={header.description}>{pathway?.description}</Typography>
            <Box className={header.categoriesWrap}>
              <CategoryLabel
                key="pathway"
                name={translate('page.pathway.pathway')}
                bgColor={colorsPalette.pink}
                textColor={theme.palette.secondary.dark}
              />
              <Box className={header.categories}>{categories}</Box>
            </Box>
          </Container>
        </Header>
        <Container>
          <div className={classes.divider} />
        </Container>
        {!hasSubscription && (
          <JoinNowBlock
            isLoggedIn={isLoggedIn}
            classes={{
              container: classes.joinNowContainer,
            }}
          />
        )}
        {hasSubscription && quizzesResults && quizzIds.length > 0 && (
          <Container>
            {quizzIds.map((item, i) => {
              const quizResults = quizzesResults[i];
              const quiz = pathway?.quizes
                ? pathway.quizes.find((quizz) => Number(quizz?.id) === item)
                : null;

              const certName = generateCertificateName(tenantTitle, pathway?.name);
              const certUrl = generateImageKitUrl({
                path: getCertificatePDFUrl(quizResults?.hash, tenantName),
                transformation: undefined,
              });

              //show only passed quizzes
              if (quizResults && quiz && quizResults?.passed) {
                return (
                  <TestSuccessBlock
                    key={quiz?.id}
                    cpdCredits={pathway?.cpd_credits}
                    svgContent={pathway?.logo_svg}
                    circleUrl={tenantCircleUrl}
                    correctQuestions={quizResults?.count_correct_answers}
                    totalQuestions={quizResults?.count_of_questions}
                    percents={quizResults?.percentage_complete}
                    filledStars={countResultStars(quizResults?.percentage_complete, quiz?.stars)}
                    onDownloadCertificate={() => onDownloadCertificate(certName, certUrl)}
                    resultHash={quizResults?.hash}
                    pathwayName={pathway?.name}
                    linkedInId={linkedInId}
                    passDate={quizResults?.date_passing}
                    category={pathway?.categories?.[0]?.title}
                    pathwaySlug={pathway?.slug}
                  />
                );
              }
              return null;
            })}
          </Container>
        )}
        <Container className={classes.mainInfoWrapper}>
          <Box className={classes.leftPanel}>
            <PathwayInformation
              cpdCredits={pathway?.cpd_credits}
              showInfo
              wrapperClass={!hasProgress ? classes.pathwayInfoWrapper : ''}
              tenantCircleUrl={tenantCircleUrl}
              pathwaySvg={pathway?.logo_svg}
            />
            {isLoggedIn && (
              <PathwayProgress
                wrapperClass={hasProgress ? classes.pathwayProgressWrapper : ''}
                videos={progressVideo as DashVideo[]}
                hasSubscription={hasSubscription}
                tests={tests}
              />
            )}
          </Box>
          <Box>
            <PathwayVideos pathwayData={pathwayData} pathwayTitle={pathwayTitle} />
            {hasSubscription && tests.length > 0 && (
              <CardWrapper>
                {tests.map((item) => (
                  <PathwayTestBlock
                    key={item.id}
                    id={item.id}
                    canPassTest={item.canPassTest}
                    testPassed={item.testPassed}
                    title={item.title}
                    link={`${DEFAULT_URL}/pathways/${slug}/test/${item.id}`}
                    wrapperClass={classes.testBlockItem}
                  />
                ))}
              </CardWrapper>
            )}
          </Box>
        </Container>
        {pathways && pathways.length > 0 && (
          <PathwaysBlock
            title={translate('page.pathway.pathwaysYouMightLike')}
            pathways={pathways as Pathway[]}
          />
        )}
        {blocks && blocks.length > 0 && <CustomPage blocks={blocks} />}
      </Box>
    </>
  );
};

export default PathwayPage;
